// the client-side table (search, sort, pages, phone cards).
import { latinDigits } from '../rules.ts';
import { $$, esc, fa, icon, SPRITE } from './core.ts';
import { normQ } from './select.ts';

/** What ds.table(el) returns: re-read the rows after a page script changed them, and the current rows. */
export interface TableApi {
  refresh(): void;
  readonly rows: TableRow[];
}
/** A row with the order it came in and its search text. */
type TableRow = HTMLTableRowElement & { _dsIndex: number; _dsText: string };
/** The table card, holding its api once wired. */
type TableHost = HTMLElement & { _dsTable?: TableApi };

// ---------------- T-072: data table (client side: search, sort, pages, phone cards) ----------------
// <ds-table> renders .ds-table-card[data-ds-table] around a plain <table>; ds.table(el) also takes a bare <table>
// (AJAX results, the DataTables bridge) and wraps it. Rows stay in the DOM (hidden, not removed), so page scripts and
// forms see every row as before.
const cellText = (td: HTMLTableCellElement | undefined): string =>
  normQ((td?.dataset.sortValue ?? td?.textContent ?? '').replace(/\s+/g, ' ').trim());
const numOf = (s: string): number | null => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- the value can arrive as a string from JSON or a data-* attribute
  const t = latinDigits(String(s))
    .replace(/[,٬\s]/g, '')
    .replace(/(ریال|تومان|٪|%)$/, '');
  return /^-?\d+(\.\d+)?$/.test(t) ? parseFloat(t) : null;
};
const EMPTY_ROW = /وجود ندارد|یافت نشد|موردی نیست|داده‌ای|اطلاعاتی/;
export function table(el: TableHost | null | undefined): TableApi | null {
  if (!el) return null;
  if (el.tagName === 'TABLE') {
    const own = el.closest<HTMLElement>('[data-ds-table]');
    if (own) el = own;
    else {
      const card = document.createElement('div');
      card.className = 'ds-table-card';
      card.dataset.dsTable = '';
      const wrap = document.createElement('div');
      wrap.className = 'ds-table-wrap';
      const host = el.closest('.table-responsive') || el;
      host.parentNode!.insertBefore(card, host);
      card.appendChild(wrap);
      wrap.appendChild(el);
      if (host !== el) host.remove();
      el.classList.add('table', 'table-hover');
      el.classList.remove('display', 'dataTable');
      el = card;
    }
  }
  if (el._dsTable) {
    el._dsTable.refresh();
    return el._dsTable;
  }
  const t = el.querySelector('table');
  if (!t) return null;
  const noun = el.dataset.noun || 'مورد';
  let size = +(el.dataset.size ?? 25),
    page = 1,
    q = '',
    chip = '',
    sortCol = -1,
    sortDir = 1,
    rows: TableRow[] = [];
  // search bar (unless data-search="off") and pager are created when the markup does not have them
  let bar = el.querySelector('.ds-filters');
  let search = el.querySelector<HTMLInputElement>('[data-ds-table-search]');
  if (!search && el.dataset.search !== 'off') {
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'ds-filters';
      el.insertBefore(bar, el.firstChild);
    }
    bar.insertAdjacentHTML(
      'afterbegin',
      `<div class="input-group ds-search-box"><span class="input-group-text">${icon('search')}</span><input class="form-control" type="search" data-ds-table-search placeholder="${esc(el.dataset.search || 'جست‌وجو در فهرست')}" aria-label="جست‌وجو در فهرست"></div>`,
    );
    search = bar.querySelector('[data-ds-table-search]');
  }
  let pager = el.querySelector<HTMLElement>('[data-ds-pager]');
  if (!pager) {
    pager = document.createElement('div');
    pager.className = 'ds-pager';
    pager.dataset.dsPager = '';
    el.appendChild(pager);
  }
  let empty = el.querySelector<HTMLElement>('.ds-table-empty');
  if (!empty) {
    empty = document.createElement('div');
    empty.className = 'ds-empty ds-table-empty';
    empty.hidden = true;
    t.closest('.ds-table-wrap')?.after(empty);
  }
  const ths = (): HTMLTableCellElement[] => [...(t.tHead?.rows[t.tHead.rows.length - 1]?.cells || [])];
  function labels(): void {
    const heads = ths().map(th => th.textContent.replace(/\s+/g, ' ').trim());
    rows.forEach(r =>
      [...r.cells].forEach((td, i) => {
        if (!td.hasAttribute('data-label') && heads[i] && td.colSpan === 1 && !td.classList.contains('ds-acts'))
          td.dataset.label = heads[i];
      }),
    );
  }
  function read(): void {
    const body = t!.tBodies[0];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- a table without <tbody> has no tBodies[0]
    rows = body ? ([...body.rows].filter(r => !r.classList.contains('ds-empty-row')) as TableRow[]) : [];
    // an old "no data" row (one wide cell) is not data
    if (
      rows.length === 1 &&
      rows[0].cells.length === 1 &&
      rows[0].cells[0].colSpan > 1 &&
      EMPTY_ROW.test(rows[0].textContent)
    ) {
      rows[0].hidden = true;
      rows = [];
    }
    rows.forEach((r, i) => {
      r._dsIndex = i;
      const x = normQ(r.textContent.replace(/\s+/g, ' '));
      r._dsText = x + ' ' + x.replace(/[,٬]/g, '');
    });
    labels();
  }
  ths().forEach((th, i) => {
    if (th.dataset.nosort !== undefined || !th.textContent.trim() || th.querySelector('input,select,button')) return;
    th.dataset.sort = '';
    th.tabIndex = 0;
    th.insertAdjacentHTML(
      'beforeend',
      `<svg class="ds-ico ds-sort" viewBox="0 0 24 24" aria-hidden="true"><use href="${SPRITE}#chev-down"></use></svg>`,
    );
    const go = (): void => {
      sortDir = sortCol === i ? -sortDir : 1;
      sortCol = i;
      ths().forEach(h => h.removeAttribute('aria-sort'));
      th.setAttribute('aria-sort', sortDir > 0 ? 'ascending' : 'descending');
      sort();
      render();
    };
    th.addEventListener('click', go);
    th.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        go();
      }
    });
  });
  function sort(): void {
    const body = t!.tBodies[0];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- a table without <tbody>
    if (!body) return;
    const ordered = [...rows];
    if (sortCol >= 0)
      ordered.sort((a, b) => {
        const x = cellText(a.cells[sortCol]),
          y = cellText(b.cells[sortCol]),
          nx = numOf(x),
          ny = numOf(y);
        return (nx !== null && ny !== null ? nx - ny : x.localeCompare(y, 'fa')) * sortDir || a._dsIndex - b._dsIndex;
      });
    ordered.forEach(r => body.appendChild(r));
    rows = ordered;
  }
  function render(): void {
    const nq = normQ(q.trim());
    const match = rows.filter(r => (!nq || r._dsText.includes(nq)) && (!chip || r.dataset.filter === chip));
    const pages = size ? Math.max(1, Math.ceil(match.length / size)) : 1;
    page = Math.min(Math.max(1, page), pages);
    const from = size ? (page - 1) * size : 0,
      to = size ? from + size : match.length;
    const on = new Set(match.slice(from, to));
    rows.forEach(r => {
      r.hidden = !on.has(r);
    });
    const none = match.length === 0;
    const wrap = t!.closest<HTMLElement>('.ds-table-wrap');
    if (wrap) wrap.hidden = none;
    empty!.hidden = !none;
    if (none) {
      const filtered = rows.length > 0;
      empty!.innerHTML =
        `<span class="ring">${icon(filtered ? 'search' : el!.dataset.emptyIcon || 'inbox')}</span><b>${esc(filtered ? 'موردی با این جست‌وجو پیدا نشد' : el!.dataset.empty || 'موردی برای نمایش نیست')}</b>` +
        (filtered
          ? '<button type="button" class="btn btn-secondary btn-sm" data-ds-table-clear>پاک کردن جست‌وجو</button>'
          : el!.dataset.emptyText
            ? `<span>${esc(el!.dataset.emptyText)}</span>`
            : '');
    }
    const sizes = [10, 25, 50, 100];
    const nums: (number | '…')[] = [];
    if (pages > 1) {
      let last = 0;
      [...new Set([1, pages, page - 1, page, page + 1])]
        .filter(p => p >= 1 && p <= pages)
        .sort((a, b) => a - b)
        .forEach(p => {
          if (p - last > 1) nums.push('…');
          nums.push(p);
          last = p;
        });
    }
    pager!.hidden = rows.length === 0;
    pager!.innerHTML =
      `<span class="ds-pager-text">${none ? 'بدون نتیجه' : `نمایش ${fa(from + 1)} تا ${fa(Math.min(to, match.length))} از ${fa(match.length)} ${esc(noun)}`}</span>` +
      (rows.length > 10
        ? `<select class="form-select form-select-sm ds-size" data-ds-native aria-label="تعداد در صفحه">${sizes.map(n => `<option value="${n}"${n === size ? ' selected' : ''}>${fa(n)} در صفحه</option>`).join('')}<option value="0"${size === 0 ? ' selected' : ''}>همه</option></select>`
        : '') +
      (pages > 1
        ? '<nav aria-label="صفحه‌ها"><ul class="pagination pagination-sm">' +
          `<li class="page-item${page <= 1 ? ' disabled' : ''}"><button type="button" class="page-link" data-p="${page - 1}" aria-label="صفحه‌ی قبل">${icon('chev-next')}</button></li>` +
          nums
            .map(p =>
              p === '…'
                ? '<li class="page-item disabled"><span class="page-link">…</span></li>'
                : `<li class="page-item${p === page ? ' active' : ''}"><button type="button" class="page-link" data-p="${p}"${p === page ? ' aria-current="page"' : ''}>${fa(p)}</button></li>`,
            )
            .join('') +
          `<li class="page-item${page >= pages ? ' disabled' : ''}"><button type="button" class="page-link" data-p="${page + 1}" aria-label="صفحه‌ی بعد">${icon('chev-prev')}</button></li></ul></nav>`
        : '');
  }
  pager.addEventListener('click', e => {
    const b = (e.target as Element).closest<HTMLElement>('[data-p]');
    if (!b || b.closest('.disabled')) return;
    page = +b.dataset.p!;
    render();
    el.scrollIntoView({ block: 'nearest' });
  });
  pager.addEventListener('change', e => {
    if ((e.target as Element).classList.contains('ds-size')) {
      size = +(e.target as HTMLSelectElement).value;
      page = 1;
      render();
    }
  });
  search?.addEventListener('input', () => {
    q = search.value;
    page = 1;
    render();
  });
  el.addEventListener('click', e => {
    if (!(e.target as Element).closest('[data-ds-table-clear]')) return;
    q = '';
    chip = '';
    if (search) search.value = '';
    $$('[data-ds-table-chips] .ds-chip', el).forEach(c => c.setAttribute('aria-pressed', String(!c.dataset.value)));
    page = 1;
    render();
    search?.focus();
  });
  $$('[data-ds-table-chips]', el).forEach(g =>
    g.addEventListener('click', e => {
      const c = (e.target as Element).closest<HTMLElement>('.ds-chip');
      if (!c) return;
      $$('.ds-chip', g).forEach(x => x.setAttribute('aria-pressed', String(x === c)));
      chip = c.dataset.value || '';
      page = 1;
      render();
    }),
  );
  read();
  render();
  const api: TableApi = {
    refresh() {
      read();
      if (sortCol >= 0) sort();
      render();
    },
    get rows() {
      return rows;
    },
  };
  el._dsTable = api;
  return api;
}
